import styled from "styled-components";
import {useAppDispatch, useAppSelector} from "../../app/hooks";
import {
    companyState,
    setSelectedCompanyWithCollaborativesAction,
} from "../../app/reducers/company-slice";
import {useDeleteCompanyWithCollaborativesMutation} from "../../query/company-query";
import theme from "../../style/theme";
import {ITEM_NUMBER_BY_PAGE} from "../../types/pages";
import {DeleteButton} from "../button/DeleteButton";
import {findPermissionLabel} from "../../utils/permission/permission";

interface Props {
}

export const CompanyWithCollaborativesListTable: React.FC<Props> = ({}) => {
    const dispatch = useAppDispatch();
    const tableHeaders = ["번호", "회사명", "전화번호", "화주사", "대행사", "권한", ""];
    const {selectedCompanyWithCollaboratives, companyWithCollaborativesPage} =
        useAppSelector(companyState);
    const deleteCompanyWithCollaborativesMutation =
        useDeleteCompanyWithCollaborativesMutation();

    const handleClickCompanyDeleteButton = async (id: string) => {
        if (window.confirm("정말로 삭제하시겠습니까?")) {
            deleteCompanyWithCollaborativesMutation.mutate(id);
        } else {
            alert("취소되었습니다.");
        }
    };

    const handleClickTableRow = (
        event: React.MouseEvent<HTMLTableRowElement>
    ) => {
        const selectedCompanyWithCollaboratives =
            companyWithCollaborativesPage.contents.find(
                (companyWithCollaboratives) =>
                    companyWithCollaboratives.id === event.currentTarget.id
            );

        if (!selectedCompanyWithCollaboratives) {
            return;
        }

        dispatch(
            setSelectedCompanyWithCollaborativesAction({
                selectedCompanyWithCollaboratives,
            })
        );
    };

    // const handleClickCompanyInfoButton = async (clientId: string) => {
    //   try {
    //     const response = await companyApi.getMobileInfo(clientId);
    //     copyClipboardJson(response.data);
    //   } catch (error) {
    //     console.log(error);
    //   }
    // };

    return (
        <CompanyTableContainer>
            <CompanyTable>
                {tableHeaders.map((tableHeader) => (
                    <CompanyTableHeader>{tableHeader}</CompanyTableHeader>
                ))}

                <CompanyTableBody>
                    {companyWithCollaborativesPage.contents.map(
                        (companyWithCollaboratives, index) => {
                            if (!companyWithCollaboratives) {
                                return null;
                            }
                            const isSelected =
                                companyWithCollaboratives.id ===
                                selectedCompanyWithCollaboratives?.id;

                            function getDisplayString<T>(array: T[], labelFunc: (item: T) => string, overflowLabel: string = "개"): string {
                                const sliceLength = 4
                                if (array.length <= sliceLength) {
                                    return array.map(labelFunc).join(", ");
                                }
                                const displayedItems = array.slice(0, sliceLength).map(labelFunc).join(", ");
                                return `${displayedItems} 외 ${array.length - sliceLength}${overflowLabel}`;
                            }

                            const clients = getDisplayString(companyWithCollaboratives.clients, client => client.name, "곳");
                            const agencies = getDisplayString(companyWithCollaboratives.agencies, client => client.name, "곳");
                            const permissions = getDisplayString(companyWithCollaboratives.roles, p => findPermissionLabel(p), "개");

                            return (
                                <CompanyTableRow
                                    key={companyWithCollaboratives.id}
                                    id={companyWithCollaboratives.id}
                                    isSelected={isSelected}
                                    onClick={handleClickTableRow}
                                >
                                    <CompanyTableData>
                                        {index +
                                            1 +
                                            companyWithCollaborativesPage.page * ITEM_NUMBER_BY_PAGE}
                                    </CompanyTableData>
                                    <CompanyNameTableData>
                                        <CompanyTableDataLabel>
                                            {companyWithCollaboratives.name}
                                        </CompanyTableDataLabel>
                                    </CompanyNameTableData>
                                    <CompanyTableData>
                                        <CompanyTableDataLabel>
                                            {companyWithCollaboratives.phone}
                                        </CompanyTableDataLabel>
                                    </CompanyTableData>
                                    <CompanyTableData>
                                        <CompanyTableDataLabel>{clients}</CompanyTableDataLabel>
                                    </CompanyTableData>
                                    <CompanyTableData>
                                        <CompanyTableDataLabel>{agencies}</CompanyTableDataLabel>
                                    </CompanyTableData>
                                    <CompanyTablePermissionLabel>{permissions}</CompanyTablePermissionLabel>
                                    <CompanyTableData>
                                        <DeleteButton
                                            value={companyWithCollaboratives.id}
                                            onClick={(
                                                event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                                            ) => {
                                                event.stopPropagation();
                                                handleClickCompanyDeleteButton(
                                                    companyWithCollaboratives.id
                                                );
                                            }}
                                        />
                                    </CompanyTableData>
                                </CompanyTableRow>
                            );
                        }
                    )}
                </CompanyTableBody>
            </CompanyTable>
        </CompanyTableContainer>
    );
};

const CompanyTableContainer = styled.div`
  height: 541px;
  width: 61vw;
  border-radius: 4px;
  word-break: break-all;
`;
const CompanyTable = styled.table`
  width: 100%;
  overflow: hidden;
  table-layout: fixed;
`;
const CompanyTableHeader = styled.th`
  height: 3.188rem;
  text-align: center;
  vertical-align: middle;
  background-color: ${theme.palette.primitives.gray50};
  font-style: normal;
  font-weight: 700;
  font-size: 1rem;
  line-height: 1.188rem;
  color: ${theme.palette.primitives.gray600};
  padding: 0 0.625rem;

  &:first-of-type {
    padding-left: 0px;
  }

  &:nth-last-of-type(1) {
    padding-right: 0px;
  }
`;
const CompanyTableBody = styled.tbody``;

interface CompanyTableRowProps {
    isSelected?: boolean;
}

const CompanyTableRow = styled.tr<CompanyTableRowProps>`
  height: 3.063rem;
  border-bottom: ${`1px solid ${theme.palette.primitives.gray100}`};
  background-color: ${(props) =>
          props.isSelected
                  ? `${theme.palette.primitives.gray50}`
                  : `${theme.palette.primitives.white}`};
  cursor: pointer;

  :hover {
    background-color: ${theme.palette.primitives.gray50};
  }
`;

const CompanyTableData = styled.td`
  padding: 6px 0px;
  text-align: center;
  vertical-align: middle;
  font-style: normal;
  font-weight: 400;
  font-size: 0.875rem;
  line-height: 1.063rem;
  color: ${theme.palette.primitives.gray600};
  padding: 0 0.625rem;

  &:first-of-type {
    padding-left: 0px;
  }

  &:nth-last-of-type(1) {
    padding-right: 0px;
  }
`;

const CompanyTablePermissionLabel = styled.td`
  padding: 6px 0px;
  text-align: center;
  vertical-align: middle;
  font-style: normal;
  font-weight: 400;
  font-size: 0.675rem;
  line-height: 1rem;
  color: ${theme.palette.primitives.gray600};
  padding: 0 0.625rem;

  &:first-of-type {
    padding-left: 0px;
  }

  &:nth-last-of-type(1) {
    padding-right: 0px;
  }
`;

const CompanyNameTableData = styled.td`
  text-align: center;
  vertical-align: middle;
  font-style: normal;
  font-weight: 500;
  font-size: 0.875rem;
  line-height: 1.063rem;
  color: ${theme.palette.primitives.gray900};
  padding: 0 0.625rem;
  max-height: 52px;
  overflow: hidden;

  @media (max-height: 52px) {
    font-size: 0.75rem;
  }
`;

const CompanyTableDataLabel = styled.div`
  margin: 0 auto;
  text-align: center;
  vertical-align: middle;
`;

const CompanyIndexButton = styled.button`
  width: 64px;
  height: 35px;
  background-color: transparent;
  border: none;
  color: #1976d2;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background-color: #1976d20a;
  }
`;

const CompanyDeleteButton = styled.button`
  width: 64px;
  height: 35px;
  border: 1px solid #d32f2f80;
  background-color: transparent;
  color: #d32f2f;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background-color: #d32f2f0a;
  }
`;
